/**
 * RematchModal — small floating panel anchored next to the match-end overlay.
 *
 * Displays the live rematch tally:
 *   "Voting for rematch... N/2 (M sec)"
 *
 * Updated by the GameClient when the server broadcasts `RematchVoteUpdate`.
 * Switches to a terminal "Rematch failed" / "Rematch ready" message before
 * being torn down. Stateless apart from the DOM node it owns.
 */
export class RematchModal {
  private parent: HTMLElement;
  private root: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private remainingMs: number = 0;
  private votes: number = 0;
  private required: number = 2;

  constructor(parent: HTMLElement) {
    this.parent = parent;
  }

  /** Show the modal in its initial "Voting for rematch..." state. */
  show(opts: { votes?: number; required?: number; remainingMs?: number } = {}): void {
    if (this.root) {
      // Already visible — just refresh state.
      this.update(opts);
      return;
    }

    this.votes = opts.votes ?? 0;
    this.required = opts.required ?? 2;
    this.remainingMs = opts.remainingMs ?? 15000;

    const root = document.createElement('div');
    root.className = 'rematch-modal';

    const status = document.createElement('div');
    status.className = 'rematch-modal-status';
    root.appendChild(status);

    this.parent.appendChild(root);
    this.root = root;
    this.statusEl = status;

    this.render();

    // Tick the countdown locally so the UI keeps moving between server pushes.
    this.countdownTimer = setInterval(() => {
      this.remainingMs = Math.max(0, this.remainingMs - 250);
      this.render();
    }, 250);
  }

  /** Update the live vote / time tally from the server. */
  update(opts: { votes?: number; required?: number; remainingMs?: number }): void {
    if (opts.votes !== undefined) this.votes = opts.votes;
    if (opts.required !== undefined) this.required = opts.required;
    if (opts.remainingMs !== undefined) this.remainingMs = opts.remainingMs;
    this.render();
  }

  /** Switch to a terminal message before fade-out (e.g. "Rematch failed"). */
  showFinalMessage(message: string): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (this.statusEl) {
      this.statusEl.textContent = message;
    }
  }

  hide(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (this.root) {
      this.root.remove();
      this.root = null;
      this.statusEl = null;
    }
  }

  isVisible(): boolean {
    return this.root !== null;
  }

  private render(): void {
    if (!this.statusEl) return;
    const sec = Math.ceil(this.remainingMs / 1000);
    this.statusEl.textContent = `Voting for rematch... ${this.votes}/${this.required} (${sec} sec)`;
  }
}
